import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useChatStore, useUIStore, useAuthStore, useCollectionStore, useToastStore, FILTER_CONFIGS } from '@/store';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import { useModalHelper } from '@/hooks/useModalHelper';
import useSidebarChats from '@/hooks/useSidebarChats';
import useSidebarAutoLoad from '@/hooks/useSidebarAutoLoad';
import SidebarListContainer from './SidebarListContainer';
import SidebarListItemChat from './SidebarListItemChat';
import SidebarPlusLockedCard from './SidebarPlusLockedCard';
import SidebarPlusLockedResultRow from './SidebarPlusLockedResultRow';
import SidebarListItemPlaceholder from '@/components/Placeholders/SidebarListItemPlaceholder';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import useResponsiveness from '@/hooks/useResponsiveness';
import getRelativeTime from '@/constants/utils/timestamp';
import { partitionSidebarChats } from '@/lib/sidebarChatSections';
import { displayChatTitle } from '@/lib/chatTitle';

const createStyles = (colors) => StyleSheet.create({
  actionsBar: {
    paddingTop: 4,
    paddingBottom: 2,
    marginBottom: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    zIndex: 2,
    backgroundColor: colors.solid_color_block,
    ...(Platform.OS === 'web' && { position: 'sticky' }),
    top: 0,
  },
  actionsBarInner: {
    height: 40,
    paddingLeft: 6,
    paddingRight: 4,
    paddingTop: 4,
    paddingBottom: 2,
    gap: 6,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    borderRadius: 8,
    backgroundColor: colors.solid_color_tertiary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    zIndex: 2,
  },
  selectedCountText: {
    marginRight: 'auto',
  },
  loadMoreButton: {
    marginVertical: 4,
    marginHorizontal: 5,
    height: 32,
  },
  paginationFooter: {
    minHeight: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noResultsContainer: {
    padding: 9,
    alignItems: 'flex-start',
  },
  noResultsText: {
    color: colors.text_color_tertiary,
  },
  doneButton: {
    marginLeft: 'auto',
  }
});

const paginationMatchesFilters = (pagination, filters) => !('filter' in pagination) || (
  pagination.filter === filters.type
  && (pagination.sort ?? null) === (filters.sort ?? null)
  && (pagination.inCollectionId ?? null) === (filters.inCollectionId ?? null)
  && !!pagination.withoutCollections === !!filters.withoutCollections
);

function SidebarChatsSection({ section = 'library', scrollContainerRef }) {
  const isPinnedSection = section === 'pinned';
  const { t } = useTranslation();
  const router = useRouter();
  const route = useCurrentRoute();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const morePending = useRef(false);
  const [failedScope, setFailedScope] = useState(null);
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { addToast } = useToastStore(state => state.actions);
  const { isXS } = useResponsiveness();

  // Library/Chats state. Metadata-only subscription — subscribing to
  // `state.chats` directly re-renders this whole section on every streaming
  // token flush (the chats array gets a new reference per flush).
  const libraryItems = useSidebarChats();
  const searchQuery = useChatStore(state => state.searchQuery);
  const searchResults = useChatStore(state => state.searchResults);
  const searchLockedResults = useChatStore(state => state.searchLockedResults);
  const searchPagination = useChatStore(state => state.searchPagination);
  const isSearching = useChatStore(state => state.isSearching);
  const searchChats = useChatStore(state => state.searchChats);
  const libraryFilters = useChatStore(state => state.filters);
  const chatPagination = useChatStore(state => state.pagination);
  const isRefreshingChats = useChatStore(state => !!state._fetchChatsPromise);
  const plusLock = useChatStore(state => state.plusLock);
  const isLoadingUser = useAuthStore(state => state.isLoadingUser);
  const pinnedConversations = useAuthStore(state => state.user?.pinnedConversations);
  const pinnedIdSet = useMemo(
    () => new Set((pinnedConversations || []).map((id) => String(id?.id ?? id?._id ?? id)).filter(Boolean)),
    [pinnedConversations],
  );

  const handleFilterSortClick = useChatStore(state => state.handleFilterSortClick);
  const fetchChats = useChatStore(state => state.fetchChats);
  const isSelectingMultipleChats = useChatStore(state => state.isSelectingMultipleChats);
  const toggleSelectingMultipleChats = useChatStore(state => state.toggleSelectingMultipleChats);
  const selectedChatIds = useChatStore(state => state.selectedChatIds);
  const addSelectedChatsToCollection = useChatStore(state => state.addSelectedChatsToCollection);
  const isArchivedFilter = libraryFilters?.type === 'archived';

  // UI State
  const collapsedSections = useUIStore(state => state.collapsedSections);
  const toggleSection = useUIStore(state => state.toggleSection);

  // Confirmation modal
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  // Collections for inline "Add to collection" action
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const collections = useMemo(() => {
    return (collectionIds || []).map(id => collectionsMap[id]).filter(Boolean);
  }, [collectionsMap, collectionIds]);

  const translatedLibraryActions = useMemo(() =>
    FILTER_CONFIGS.library.actions.map(action => ({
      ...action,
      id: action.id === 'archive' && isArchivedFilter ? 'unarchive' : action.id,
      title: action.id === 'delete'
        ? t('common.delete')
        : action.id === 'archive'
          ? (isArchivedFilter ? t('dropdowns.unarchive') : t('common.archive'))
          : t('sidebar.addToCollection'),
    })), [t, isArchivedFilter]);

  // Memoize the isLibraryChat function to prevent recreation
  const isLibraryChat = useCallback((id) => route.isLibraryChat(id), [route]);

  // Memoize the chat items to prevent unnecessary re-renders
  const displayedItems = useMemo(() => {
    const items = searchQuery ? searchResults : libraryItems;
    const filtered = items.filter(item => {
      if (!searchQuery && libraryFilters?.type !== 'archived' && ['ios', 'android'].includes(Platform.OS) && item.hasMacProject) return false;
      if (!item.isOwner) return false;
      // Skip temporary chats
      if (item?.flags?.isTemporary === true) return false;
      if (!searchQuery && libraryFilters?.type === 'non-work' && (item.isArchived || item.flags?.isCoworkerSession === true)) return false;
      // Final UI-level gate for hidden tool-run backing conversations.
      // The backend filters them out and the chat store filters them on
      // ingest, but we render-time filter too as defense-in-depth so a
      // single missed filter upstream can't leak them into the sidebar.
      // See docs/TOOLS.md → "Result Storage & History".
      if (item?.flags?.isToolRun === true && !item?.flags?.isToolPromotedToChat) return false;
      // Collection chats normally stay inside their collection, but a pin is
      // an explicit request to keep the chat visible in the main library.
      if (!searchQuery && item.inCollectionId && !pinnedIdSet.has(String(item.id))) return false;
      return true;
    });

    // The chat store always re-sorts items newest-first via sortChatsWithPinned,
    // so we re-apply the user's chosen Newest/Oldest sort here. Pinned chats
    // stay at the top regardless of sort direction.
    const oldestFirst = libraryFilters?.sort === 'oldest';
    const sorted = [...filtered].sort((a, b) => {
      // Auth pin ids are authoritative even when the set is empty. Falling
      // back to persisted chat.isPinned in that case resurrects stale pins.
      const aPinned = pinnedIdSet.has(String(a.id));
      const bPinned = pinnedIdSet.has(String(b.id));
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return oldestFirst ? aTime - bTime : bTime - aTime;
    });

    return sorted.map(item => {
      // lastMessageTime is computed at fetch/persist time and ages, so derive it
      // live from updatedAt/createdAt — keeps timestamps showing on macOS even
      // when the persisted value is stale or empty after rehydration.
      const liveTime = getRelativeTime(item.updatedAt || item.createdAt);
      return {
        id: item.id,
        agentId: item.agentId,
        title: displayChatTitle(item.title),
        href: item.href,
        isPinned: pinnedIdSet.has(String(item.id)),
        isArchived: item.isArchived,
        visibility: item.visibility,
        isCoworkerSession: item?.flags?.isCoworkerSession === true,
        lastMessageTime: liveTime || item.lastMessageTime || '',
        inCollectionId: item.inCollectionId,
        isActive: isLibraryChat(item.id),
        isGenerating: item.isInferencing === true || item.isInferencingWaiting === true,
      };
    });
  }, [searchQuery, searchResults, libraryItems, isLibraryChat, libraryFilters?.type, libraryFilters?.sort, pinnedIdSet]);

  const { pinnedItems, recentItems } = useMemo(
    () => partitionSidebarChats(displayedItems, !!searchQuery), [displayedItems, searchQuery],
  );
  const sectionItems = isPinnedSection ? pinnedItems : recentItems;
  const sectionKey = isPinnedSection ? 'pinned' : 'library';

  const handleLibraryLabelClick = useCallback((labelId) => {
    if (labelId === null) {
      router.push('/library');
    } else {
      handleFilterSortClick(labelId, router);
    }
  }, [router, handleFilterSortClick]);

  const handleLibraryActionClick = useCallback((actionId, payload) => {
    try {
      if (actionId === 'delete') {
        const deleteSelectedChats = useChatStore.getState().deleteSelectedChats;
        const count = selectedChatIds.length;
        if (count === 0) return;
        showConfirmationModal({
          title: `${t('common.delete')} ${count} ${count === 1 ? 'chat' : 'chats'}?`,
          message: t('chat.delete.message'),
          confirmText: t('common.delete'),
          cancelText: t('common.cancel'),
          isDangerous: true,
          onConfirm: () => {
            deleteSelectedChats();
            addToast(`Deleted ${count} ${count === 1 ? 'chat' : 'chats'}`, 'success');
          },
        });
      } else if (actionId === 'archive') {
        const archiveSelectedChats = useChatStore.getState().archiveSelectedChats;
        const count = selectedChatIds.length;
        if (count === 0) return;
        showConfirmationModal({
          title: `${t('common.archive')} ${count} ${count === 1 ? 'chat' : 'chats'}?`,
          message: t('chat.archive.message'),
          confirmText: t('common.archive'),
          cancelText: t('common.cancel'),
          onConfirm: () => {
            archiveSelectedChats();
            addToast(`Archived ${count} ${count === 1 ? 'chat' : 'chats'}`, 'success');
          },
        });
      } else if (actionId === 'unarchive') {
        const unarchiveSelectedChats = useChatStore.getState().unarchiveSelectedChats;
        const count = selectedChatIds.length;
        if (count === 0) return;
        showConfirmationModal({
          title: `${t('dropdowns.unarchive')} ${count} ${count === 1 ? 'chat' : 'chats'}?`,
          message: t('chat.archive.unarchiveMessage'),
          confirmText: t('chat.archive.unarchiveButton'),
          cancelText: t('common.cancel'),
          onConfirm: () => {
            unarchiveSelectedChats();
            addToast(`Unarchived ${count} ${count === 1 ? 'chat' : 'chats'}`, 'success');
          },
        });
      } else if (actionId === 'add-to-collection') {
        const addSelectedChatsToCollection = useChatStore.getState().addSelectedChatsToCollection;
        const count = selectedChatIds.length;
        const collectionId = payload?.collectionId;
        if (!collectionId || count === 0) return;
        const collectionTitle = (collections || []).find(c => c.id === collectionId)?.title || 'Collection';
        showConfirmationModal({
          title: `${t('common.add')} ${count} ${count === 1 ? 'chat' : 'chats'} to ${collectionTitle}?`,
          message: 'Selected chats will be moved into this collection.',
          confirmText: t('common.add'),
          cancelText: t('common.cancel'),
          onConfirm: () => {
            addSelectedChatsToCollection(collectionId);
            addToast(
              `Added ${count} ${count === 1 ? 'chat' : 'chats'} to ${collectionTitle}`,
              'success',
              [
                {
                  title: t('sidebar.viewCollection'),
                  href: `/collections/${collectionId}`,
                }
              ]
            );
          },
        });
      } else if (actionId === 'select-multiple') {
        toggleSelectingMultipleChats();
      } else if (actionId === 'done-selecting') {
        toggleSelectingMultipleChats();
      } else {
        console.log('Library action selected:', actionId, payload);
      }
    } catch (error) {
      console.error('Error performing library action:', error);
    }
  }, [toggleSelectingMultipleChats, selectedChatIds, showConfirmationModal, collections, addToast, t]);

  const getCurrentLibraryPath = useCallback(() => {
    const path = [];
    if (libraryFilters.type) {
      const filterLabel = FILTER_CONFIGS.library.filters
        .find(l => l.id === libraryFilters.type);
      if (filterLabel) path.push(filterLabel);
    }
    if (libraryFilters.sort) {
      const sortLabel = FILTER_CONFIGS.library.sort
        .find(l => l.id === libraryFilters.sort);
      if (sortLabel) path.push(sortLabel);
    }
    return path;
  }, [libraryFilters]);

  const pagination = searchQuery ? searchPagination : chatPagination;
  const paginationScope = JSON.stringify([searchQuery, libraryFilters, pagination.current]);
  const loadMoreFailed = failedScope === paginationScope;
  const handleLoadMore = useCallback(async () => {
    const state = useChatStore.getState();
    const current = searchQuery ? state.searchPagination : state.pagination;
    if (morePending.current || state.isSearching || state._fetchChatsPromise || isLoadingUser
      || !current.hasMore || state.searchQuery !== searchQuery
      || (!searchQuery && !paginationMatchesFilters(current, state.filters))) return;
    morePending.current = true;
    setIsLoadingMore(true);
    setFailedScope(null);
    try {
      const nextPage = current.current + 1;
      if (searchQuery) await searchChats(searchQuery, nextPage);
      else await fetchChats(nextPage);
      const next = useChatStore.getState();
      const updated = searchQuery ? next.searchPagination : next.pagination;
      // Stop automatic retries if a refresh retired the response or the server
      // failed to advance the page. A failed network request also pauses here.
      if (updated.current < nextPage && updated.hasMore) setFailedScope(paginationScope);
    } catch (error) {
      setFailedScope(paginationScope);
      console.error('Error loading more sidebar chats:', error);
    } finally {
      morePending.current = false;
      setIsLoadingMore(false);
    }
  }, [searchQuery, paginationScope, fetchChats, searchChats, isLoadingUser]);

  const footerRef = useSidebarAutoLoad({
    scrollContainerRef,
    enabled: !isPinnedSection && (!!searchQuery || !collapsedSections[sectionKey])
      && pagination.hasMore && !isSearching && !isLoadingUser && !isRefreshingChats
      && (!!searchQuery || paginationMatchesFilters(pagination, libraryFilters))
      && !isLoadingMore && !loadMoreFailed,
    loadMore: handleLoadMore,
  });

  if (isPinnedSection && (searchQuery || !pinnedItems.length)) return null;

  return (
    <>
      <SidebarListContainer
        title={isPinnedSection ? t('sidebar.pinned') : searchQuery ? t('navigation.searchResults') : t('navigation.library')}
        labels={isPinnedSection || searchQuery ? [] : [
          { id: 'filters', title: t('sidebar.filters'), children: FILTER_CONFIGS.library.filters },
          { 
            id: 'actions', 
            title: t('sidebar.actions'), 
            children: isSelectingMultipleChats 
              ? [...translatedLibraryActions, { id: 'done-selecting', title: t('sidebar.doneSelecting') }] 
              : [{ id: 'select-multiple', title: t('sidebar.selectMultiple') }]
          },
          { id: 'sort', title: t('sidebar.sort'), children: FILTER_CONFIGS.library.sort }
        ]}
        currentLabelPath={isPinnedSection ? [] : getCurrentLibraryPath()}
        isCollapsed={!searchQuery && collapsedSections[sectionKey]}
        onToggle={() => toggleSection(sectionKey)}
        onLabelClick={isPinnedSection ? () => toggleSection(sectionKey) : handleLibraryLabelClick}
        onActionClick={handleLibraryActionClick}
        stickyIndexTop={2}
      />
      {(searchQuery || !collapsedSections[sectionKey]) && (
        <>
          {!isPinnedSection && isSelectingMultipleChats && (
            <View style={styles.actionsBar}>
              <View style={styles.actionsBarInner}>
                <Text style={styles.selectedCountText} size='s'>
                  {selectedChatIds.length} {isXS ? '' : t('sidebar.selected')}
                </Text>
                <Button
                  size="small"
                  variant="tertiary"
                  prependIcon="trash"
                  tooltip={t('common.delete')}
                  iconColor={colors.color_destructive}
                  isDisabled={selectedChatIds.length === 0}
                  onPress={() => handleLibraryActionClick('delete')}
                />
                <Button
                  size="small"
                  variant="tertiary"
                  prependIcon={isArchivedFilter ? "box-open" : "box-archive"}
                  tooltip={isArchivedFilter ? t('dropdowns.unarchive') : t('common.archive')}
                  isDisabled={selectedChatIds.length === 0}
                  onPress={() => handleLibraryActionClick(isArchivedFilter ? 'unarchive' : 'archive')}
                />
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger>
                    <View>
                      <Button
                        size="small"
                        variant="tertiary"
                        prependIcon="folder-plus"
                        tooltip={t('sidebar.addToCollection')}
                        isDisabled={selectedChatIds.length === 0}
                      />
                    </View>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content className="DropdownMenuContent" align="start" sideOffset={4}>
                    {collections.length > 0 ? (
                      collections.map((collection) => (
                        <DropdownMenu.Item
                          key={`inline-collection-${collection.id}`}
                          className="DropdownMenuItem"
                          onSelect={() => addSelectedChatsToCollection(collection.id)}
                        >
                          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                            {collection.title}
                          </DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                      ))
                    ) : (
                      <DropdownMenu.Item className="DropdownMenuItem" disabled>
                        <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">{t('sidebar.noCollections')}</DropdownMenu.ItemTitle>
                      </DropdownMenu.Item>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
                <Button
                  size="small"
                  variant="tertiary"
                  // prependIcon="square-check"
                  tooltip={t('sidebar.doneSelecting')}
                  onPress={() => handleLibraryActionClick('done-selecting')}
                >
                  <Text size='m' color='secondary'>{t('sidebar.done')}</Text>
                </Button>
              </View>
            </View>
          )}
          {((isLoadingUser && displayedItems.length === 0) || (isSearching && !isLoadingMore)) ? (
            // Show 8 placeholder items while loading. The chat store persists
            // chat metadata locally, so on a warm start we already have rows
            // to paint — keep showing those instead of blanking the list to
            // skeletons until /json/user/me responds (it also refreshes
            // in-place once the response lands).
            Array.from({ length: 8 }).map((_, index) => (
              <SidebarListItemPlaceholder key={`placeholder-${index}`} />
            ))
          ) : (
            <>
              {sectionItems.map((item, index) => (
                <SidebarListItemChat key={`sidebar-list-item-chat-${item?.id || index}`} {...item} hideMetadataUntilHover />
              ))}
              {/*
                Plus win-back: opaque "[Plus-only result]" rows for search
                matches that hit conversations the user lost access to.
                Backend already partitions; we just render. They appear
                after the normal results so unlocked matches stay
                prominent (and to satisfy the "ease of cancellation"
                principle's cousin — we never bury real data behind a
                wall). See PLUS_WINBACK_PRESSURE.md §1.
              */}
              {searchQuery && searchLockedResults && searchLockedResults.length > 0 && (
                searchLockedResults.map(item => (
                  <SidebarPlusLockedResultRow
                    key={`sidebar-list-item-locked-${item.id}`}
                    id={item.id}
                    updatedAt={item.updatedAt}
                  />
                ))
              )}
              {!isPinnedSection && pagination.hasMore && (
                Platform.OS === 'web' ? (
                  <View ref={footerRef} style={styles.paginationFooter}>
                    {isLoadingMore && <ActivityIndicator size="small" color={colors.text_color_tertiary} accessibilityLabel={t('common.loading')} />}
                    {loadMoreFailed && <Button text={t('common.retry')} variant="tertiary" size="small" onPress={handleLoadMore} />}
                  </View>
                ) : (
                  <LoadMoreButton onPress={handleLoadMore} isLoading={isLoadingMore} style={styles.loadMoreButton} />
                )
              )}
              {searchQuery && !searchResults.length && (!searchLockedResults || !searchLockedResults.length) && !isSearching && (
                <View style={styles.noResultsContainer}>
                  <Text style={styles.noResultsText}>
                    {t('search.noResults')}
                  </Text>
                </View>
              )}
              {/*
                Locked-history card. Only renders for churned-Plus users
                with > visibleCap conversations and only in the default
                (non-search) sidebar view. Search-mode pressure is
                handled by SidebarPlusLockedResultRow above.
              */}
              {!isPinnedSection && !searchQuery && plusLock?.isChurnedPlus && plusLock?.hiddenCount > 0 && (
                <SidebarPlusLockedCard
                  hiddenCount={plusLock.hiddenCount}
                  lockedPreview={plusLock.lockedPreview}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export default React.memo(SidebarChatsSection);
